import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Alert,
  InputAdornment,
  Skeleton,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Share as ReferralIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/core';
import { referralApi } from '../../api';
import type { ReferralCode, ReferralStats, ReferralRewardTier, Referral } from '../../api';
import { ReferralsEducationModal } from '../../components/education';
import { hasSeenEducationModal } from '../../lib/educationModals';

// Helper function for clipboard operations with fallback
const copyToClipboard = async (text: string): Promise<boolean> => {
  // Try modern clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback:', err);
    }
  }

  // Fallback for non-secure contexts or when clipboard API fails
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    // Make textarea visible but off-screen to ensure it can receive focus
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    textArea.style.zIndex = '99999';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, text.length); // For mobile devices
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

export function MyReferralsPage() {
  useDocumentTitle('My Referrals');

  const [referralCode, setReferralCode] = useState<ReferralCode | null>(null);
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [rewardTiers, setRewardTiers] = useState<ReferralRewardTier[]>([]);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [educationModalOpen, setEducationModalOpen] = useState(() => !hasSeenEducationModal('referrals'));

  const loadReferralData = useCallback(async () => {
    try {
      setLoading(true);
      const [code, stats, tiers, referralsList] = await Promise.all([
        referralApi.getMyCode(),
        referralApi.getStats(),
        referralApi.getTiers(),
        referralApi.getReferrals(),
      ]);
      setReferralCode(code);
      setReferralStats(stats);
      setRewardTiers(tiers);
      setReferrals(referralsList);
    } catch (err) {
      console.error('Failed to load referral data:', err);
      setError('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReferralData();
  }, [loadReferralData]);

  const referralUrl = referralCode
    ? `${window.location.origin}/r/${referralCode.code}`
    : '';

  const handleCopyReferralLink = async () => {
    if (!referralUrl) return;
    const success = await copyToClipboard(referralUrl);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleRegenerateCode = async () => {
    setRegenerating(true);
    setError(null);
    try {
      const newCode = await referralApi.regenerateCode();
      setReferralCode(newCode);
    } catch (err: unknown) {
      const error = err as { daysRemaining?: number; message?: string };
      if (error.daysRemaining) {
        setError(`You can regenerate your code in ${error.daysRemaining} days`);
      } else {
        setError(error.message || 'Failed to regenerate code');
      }
    } finally {
      setRegenerating(false);
    }
  };

  const getNextTier = () => {
    if (!referralStats || rewardTiers.length === 0) return null;
    const sortedTiers = [...rewardTiers].sort((a, b) => a.minReferrals - b.minReferrals);
    return sortedTiers.find(tier => tier.minReferrals > referralStats.totalReferrals) || null;
  };

  const nextTier = getNextTier();
  const progressToNextTier = nextTier && referralStats
    ? (referralStats.totalReferrals / nextTier.minReferrals) * 100
    : 100;

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ReferralIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4">My Referrals</Typography>
        <Tooltip title="Learn more about referrals">
          <IconButton
            component={RouterLink}
            to="/support/credits#referral-program"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Share your unique referral link with friends. When they sign up, you'll earn free board credits!
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" height={300} />
        </Box>
      ) : (
        <>
          {/* Referral Link & Stats */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Your Referral Link
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <TextField
                fullWidth
                value={referralUrl}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copySuccess ? 'Copied!' : 'Copy link'}>
                        <IconButton onClick={handleCopyReferralLink} edge="end">
                          <ContentCopyIcon color={copySuccess ? 'success' : 'inherit'} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
              <Tooltip title="Generate new code">
                <IconButton
                  onClick={handleRegenerateCode}
                  disabled={regenerating}
                  size="small"
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
              Code: <strong>{referralCode?.code}</strong>
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Stats */}
            <Typography variant="h6" gutterBottom>
              Your Stats
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h3" color="primary">
                    {referralStats?.totalReferrals || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Referrals
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="h3" color="success.main">
                    {referralStats?.creditsEarned || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Credits Earned
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, height: '100%' }}>
                  {nextTier ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">
                          Progress to {nextTier.name}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {referralStats?.totalReferrals || 0} / {nextTier.minReferrals}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progressToNextTier}
                        sx={{ height: 10, borderRadius: 1, mb: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        +{nextTier.creditsAwarded} credits at {nextTier.minReferrals} referrals
                      </Typography>
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Chip label="All tiers unlocked!" color="success" />
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Reward Tiers */}
            <Typography variant="h6" gutterBottom>
              Reward Tiers
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {rewardTiers
                .sort((a, b) => a.minReferrals - b.minReferrals)
                .map((tier) => {
                  const isUnlocked = (referralStats?.totalReferrals || 0) >= tier.minReferrals;
                  return (
                    <Chip
                      key={tier.id}
                      label={`${tier.name}: ${tier.minReferrals} referrals = +${tier.creditsAwarded} credits`}
                      variant={isUnlocked ? 'filled' : 'outlined'}
                      color={isUnlocked ? 'success' : 'default'}
                    />
                  );
                })}
            </Box>
          </Paper>

          {/* Referrals List */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              People You've Referred
            </Typography>

            {referrals.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  You haven't referred anyone yet. Share your link to get started!
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Joined</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referrals.map((referral) => (
                      <TableRow key={referral.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {referral.referredUser?.displayName || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {referral.referredUser?.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(referral.createdAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      <ReferralsEducationModal
        open={educationModalOpen}
        onClose={() => setEducationModalOpen(false)}
      />
    </Box>
  );
}
